import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [isCaseStudiesOpen, setIsCaseStudiesOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsToolsOpen(false);
    setIsCaseStudiesOpen(false);
  }, [location.pathname]);
  const isActive = (path: string) => location.pathname === path;
  const serviceLinks = [{
    name: 'AI Lead Conversion',
    path: '/ai-lead-conversion'
  }, {
    name: 'Custom Automations',
    path: '/custom-automations'
  }, {
    name: 'CRM & Business Tools',
    path: '/crm-business-tools'
  }, {
    name: 'Reputation Management',
    path: '/reputation-management'
  }, {
    name: 'Ad Campaigns',
    path: '/ad-campaigns'
  }, {
    name: 'Web Design & SEO',
    path: '/web-design-seo'
  }, {
    name: 'Google Business Profile Setup',
    path: '/google-business-profile-setup'
  }];
  const toolLinks = [{
    name: 'Gmail Signature Generator',
    path: '/tools/gmail-signature-generator'
  }];
  const caseStudyLinks = [{
    name: 'All Case Studies',
    path: '/case-studies'
  }, {
    name: 'TFP Landscaping',
    path: '/case-studies/tfp-landscaping'
  }, {
    name: 'Finish Coat Painting',
    path: '/case-studies/finish-coat-painting'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-viridia-black/90 backdrop-blur-md py-3 shadow-lg' : 'bg-transparent py-5'}`} data-id="element-275" data-mp="2wcyj22c9oji6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-276" data-mp="361q6pyaaohna">
        <div className="flex justify-between items-center" data-id="element-277" data-mp="364cg83gqx785">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-id="element-278" data-mp="2iei1thgs3als">
            <img src="https://cdn.magicpatterns.com/uploads/dBgonnHgNocdZqWCx2rT29/VA-Logo_(1).png" alt="Viridia Analytics" className="h-14 w-auto object-contain transition-transform group-hover:scale-105" data-id="element-279" data-mp="2dedtieq0xm6n" />
            <span aria-hidden="true" className="text-white font-bold text-xl tracking-wide hidden sm:block" data-id="element-280" data-mp="7k441teyh840">
              VIRIDIA ANALYTICS
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav aria-label="Main navigation" className="hidden md:flex items-center gap-8" data-id="element-281" data-mp="a37le5da5iag">
            <Link to="/" className={`text-sm font-medium transition-colors hover:text-viridia ${isActive('/') ? 'text-viridia' : 'text-gray-300'}`} data-id="element-282" data-mp="f1iugdvr3orm">
              Home
            </Link>

            <Link to="/about" className={`text-sm font-medium transition-colors hover:text-viridia ${isActive('/about') ? 'text-viridia' : 'text-gray-300'}`} data-id="element-283" data-mp="sus0c4khps5e">
              About
            </Link>

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => setIsServicesOpen(true)} onMouseLeave={() => setIsServicesOpen(false)} data-id="element-284" data-mp="o4jwo3hhwvq0">
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-viridia ${serviceLinks.some(link => isActive(link.path)) ? 'text-viridia' : 'text-gray-300'}`} data-id="element-285" data-mp="1bsbaxcmljitx">
                Services
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} data-id="element-286" data-mp="3k22nv6m94ar8" />
              </button>

              <AnimatePresence data-id="element-287" data-mp="3b37h9tfaoyne">
                {isServicesOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-56 bg-viridia-surface rounded-lg shadow-xl border border-gray-800 overflow-hidden" data-id="element-288" data-mp="bmr9u656e9qj">
                    <div className="py-2" data-id="element-289" data-mp="28av69zrw9xox">
                      {serviceLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2 text-sm transition-colors hover:bg-viridia-gray hover:text-viridia ${isActive(link.path) ? 'text-viridia bg-viridia-gray/50' : 'text-gray-300'}`} data-id="element-290" data-mp="2d9fwd6dfn0zn">
                          {link.name}
                        </Link>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Case Studies Dropdown */}
            <div className="relative" onMouseEnter={() => setIsCaseStudiesOpen(true)} onMouseLeave={() => setIsCaseStudiesOpen(false)} data-id="element-291" data-mp="34yrhsq8h62ta">
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-viridia ${caseStudyLinks.some(link => isActive(link.path)) ? 'text-viridia' : 'text-gray-300'}`} data-id="element-292" data-mp="3ez1um9qll87x">
                Case Studies
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isCaseStudiesOpen ? 'rotate-180' : ''}`} data-id="element-293" data-mp="1olaeskhfsejn" />
              </button>

              <AnimatePresence data-id="element-294" data-mp="1xm8kd77iwu4c">
                {isCaseStudiesOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-56 bg-viridia-surface rounded-lg shadow-xl border border-gray-800 overflow-hidden" data-id="element-295" data-mp="1xkfevv8ncfxx">
                    <div className="py-2" data-id="element-296" data-mp="2oth5oemby60e">
                      {caseStudyLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2 text-sm transition-colors hover:bg-viridia-gray hover:text-viridia ${isActive(link.path) ? 'text-viridia bg-viridia-gray/50' : 'text-gray-300'}`} data-id="element-297" data-mp="2a54514caryzk">
                          {link.name}
                        </Link>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <Link to="/pricing" className={`text-sm font-medium transition-colors hover:text-viridia ${isActive('/pricing') ? 'text-viridia' : 'text-gray-300'}`} data-id="element-298" data-mp="1w5blbsz6uj13">
              Pricing
            </Link>

            {/* Free Tools Dropdown */}
            <div className="relative" onMouseEnter={() => setIsToolsOpen(true)} onMouseLeave={() => setIsToolsOpen(false)} data-id="element-299" data-mp="2e8fu73lwawfs">
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-viridia ${toolLinks.some(link => isActive(link.path)) ? 'text-viridia' : 'text-gray-300'}`} data-id="element-300" data-mp="24j62houyrfs7">
                Free Tools
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isToolsOpen ? 'rotate-180' : ''}`} data-id="element-301" data-mp="1qk6aq8c5mkrt" />
              </button>

              <AnimatePresence data-id="element-302" data-mp="1ln651yj7vk18">
                {isToolsOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-64 bg-viridia-surface rounded-lg shadow-xl border border-gray-800 overflow-hidden" data-id="element-303" data-mp="1gna9mnol50xa">
                    <div className="py-2" data-id="element-304" data-mp="14vjy0e5vjww">
                      {toolLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2 text-sm transition-colors hover:bg-viridia-gray hover:text-viridia ${isActive(link.path) ? 'text-viridia bg-viridia-gray/50' : 'text-gray-300'}`} data-id="element-305" data-mp="2zn16vsmmhtxd">
                          {link.name}
                        </Link>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block" data-id="element-306" data-mp="cv28sk9vwlnw">
            <Link to="/contact" className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-medium text-white bg-viridia rounded-full hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_15px_rgba(16,185,129,0.4)] hover:-translate-y-0.5" data-id="element-307" data-mp="gxylxvgnnpkg">
              Book a Call
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-gray-300 hover:text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-308" data-mp="3u6bg179obw6b">
            {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-309" data-mp="7vz4x35n4lhi" /> : <MenuIcon className="w-6 h-6" data-id="element-310" data-mp="1ed2sfxbgv4ux" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <AnimatePresence data-id="element-311" data-mp="1jbe4mep8hrgb">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="md:hidden bg-viridia-black border-t border-gray-800 overflow-hidden" data-id="element-312" data-mp="33vj93mpwxbmm">
            <div className="px-4 pt-2 pb-6 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto" data-id="element-313" data-mp="2g5ow955lfn2d">
              <Link to="/" className={`block px-3 py-3 rounded-md text-base font-medium ${isActive('/') ? 'text-viridia bg-viridia-surface' : 'text-gray-300 hover:text-white hover:bg-viridia-surface'}`} data-id="element-314" data-mp="2b6m0kbbi6t72">
                Home
              </Link>
              <Link to="/about" className={`block px-3 py-3 rounded-md text-base font-medium ${isActive('/about') ? 'text-viridia bg-viridia-surface' : 'text-gray-300 hover:text-white hover:bg-viridia-surface'}`} data-id="element-315" data-mp="9cv3bl68pn75">
                About
              </Link>

              <div className="px-3 py-2" data-id="element-316" data-mp="1oy6up1vjnidt">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-317" data-mp="1oz84z44qk0uh">
                  Services
                </div>
                <div className="space-y-1 pl-3 border-l border-gray-800" data-id="element-318" data-mp="1tux6hd0bfrtf">
                  {serviceLinks.map(link => <Link key={link.path} to={link.path} className={`block px-3 py-2 rounded-md text-base font-medium ${isActive(link.path) ? 'text-viridia' : 'text-gray-300 hover:text-white'}`} data-id="element-319" data-mp="2mmgxvk72gc6i">
                      {link.name}
                    </Link>)}
                </div>
              </div>

              <div className="px-3 py-2" data-id="element-320" data-mp="11944v6qraj88">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-321" data-mp="119xce9xjhcrf">
                  Case Studies
                </div>
                <div className="space-y-1 pl-3 border-l border-gray-800" data-id="element-322" data-mp="oqi7uqjk19fj">
                  {caseStudyLinks.map(link => <Link key={link.path} to={link.path} className={`block px-3 py-2 rounded-md text-base font-medium ${isActive(link.path) ? 'text-viridia' : 'text-gray-300 hover:text-white'}`} data-id="element-323" data-mp="13ly7db2wlxjb">
                      {link.name}
                    </Link>)}
                </div>
              </div>

              <Link to="/pricing" className={`block px-3 py-3 rounded-md text-base font-medium ${isActive('/pricing') ? 'text-viridia bg-viridia-surface' : 'text-gray-300 hover:text-white hover:bg-viridia-surface'}`} data-id="element-324" data-mp="kpy9xkr5q1sc">
                Pricing
              </Link>

              <div className="px-3 py-2" data-id="element-325" data-mp="3je57ti6tmbwm">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-326" data-mp="2mna5pweh6nwk">
                  Free Tools
                </div>
                <div className="space-y-1 pl-3 border-l border-gray-800" data-id="element-327" data-mp="2qp57t348d5be">
                  {toolLinks.map(link => <Link key={link.path} to={link.path} className={`block px-3 py-2 rounded-md text-base font-medium ${isActive(link.path) ? 'text-viridia' : 'text-gray-300 hover:text-white'}`} data-id="element-328" data-mp="2vlvkrhkqnksl">
                      {link.name}
                    </Link>)}
                </div>
              </div>

              <div className="pt-4 pb-2" data-id="element-329" data-mp="3ecd6v3b2fi4z">
                <Link to="/contact" className="block w-full text-center px-6 py-3 text-base font-medium text-white bg-viridia rounded-md hover:bg-viridia-dark transition-colors" data-id="element-330" data-mp="3jc803dibcjt7">
                  Book a Call
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}